/**
 * The planner's own state: her routes, which route runs on which day, her edits to the
 * suggestions, spares per day and what's been loaded into the car.
 *
 * This is INTENT, kept apart from the placement record (the event log). Pure functions: each
 * action returns a new state; the store persists it.
 */
import type { CalendarDate, RouteDef } from "@hitl/rotations-domain";
import { addDays } from "@hitl/rotations-domain";

export interface PlannerState {
  readonly routes: readonly RouteDef[];
  /** Day → route id. */
  readonly schedule: Readonly<Record<string, string>>;
  /** Day → (spot → pot) her edits. */
  readonly pins: Readonly<Record<string, Readonly<Record<string, string>>>>;
  /** Day → number of spare pots to load. */
  readonly spares: Readonly<Record<string, number>>;
  /** Day → pots scanned into the car. */
  readonly loaded: Readonly<Record<string, readonly string[]>>;
}

export const DEFAULT_SPARES = 2;

export const emptyPlanner: PlannerState = { routes: [], schedule: {}, pins: {}, spares: {}, loaded: {} };

function without<T>(record: Readonly<Record<string, T>>, key: string): Record<string, T> {
  const { [key]: _gone, ...rest } = record;
  return rest;
}

export function placeRoute(s: PlannerState, on: CalendarDate, routeId: string | null): PlannerState {
  // A new route on a day makes that day's edits and loading meaningless.
  const cleared = { ...s, pins: without(s.pins, on), loaded: without(s.loaded, on) };
  return routeId ? { ...cleared, schedule: { ...s.schedule, [on]: routeId } } : { ...cleared, schedule: without(s.schedule, on) };
}

export function saveRoute(s: PlannerState, route: RouteDef): PlannerState {
  const exists = s.routes.some((r) => r.id === route.id);
  return { ...s, routes: exists ? s.routes.map((r) => (r.id === route.id ? route : r)) : [...s.routes, route] };
}

export function deleteRoute(s: PlannerState, routeId: string): PlannerState {
  const schedule = Object.fromEntries(Object.entries(s.schedule).filter(([, id]) => id !== routeId));
  return { ...s, routes: s.routes.filter((r) => r.id !== routeId), schedule };
}

export function pinPot(s: PlannerState, on: CalendarDate, slotId: string, potId: string): PlannerState {
  return { ...s, pins: { ...s.pins, [on]: { ...(s.pins[on] ?? {}), [slotId]: potId } } };
}

export function unpin(s: PlannerState, on: CalendarDate, slotId: string): PlannerState {
  const day = without(s.pins[on] ?? {}, slotId);
  return { ...s, pins: Object.keys(day).length ? { ...s.pins, [on]: day } : without(s.pins, on) };
}

/** "Re-suggest": drop her edits for one day, or for that day and every later day. */
export function clearPins(s: PlannerState, from: CalendarDate, scope: "day" | "rest"): PlannerState {
  const pins = Object.fromEntries(Object.entries(s.pins).filter(([on]) => (scope === "day" ? on !== from : on < from)));
  return { ...s, pins };
}

export function setSpares(s: PlannerState, on: CalendarDate, count: number): PlannerState {
  return { ...s, spares: { ...s.spares, [on]: Math.max(0, Math.min(10, Math.round(count))) } };
}

export function sparesOn(s: PlannerState, on: CalendarDate, fallback = DEFAULT_SPARES): number {
  return s.spares[on] ?? fallback;
}

export function markLoaded(s: PlannerState, on: CalendarDate, potId: string): PlannerState {
  const list = s.loaded[on] ?? [];
  return list.includes(potId) ? s : { ...s, loaded: { ...s.loaded, [on]: [...list, potId] } };
}

export function unmarkLoaded(s: PlannerState, on: CalendarDate, potId: string): PlannerState {
  return { ...s, loaded: { ...s.loaded, [on]: (s.loaded[on] ?? []).filter((p) => p !== potId) } };
}

/* ── Calendar maths ───────────────────────────────────────────────────── */

/** Monday of the week containing `on`. */
export function weekStart(on: CalendarDate): CalendarDate {
  const [y, m, d] = on.split("-").map(Number) as [number, number, number];
  const weekday = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7; // Mon = 0
  return addDays(on, -weekday);
}

export function weekDays(on: CalendarDate): CalendarDate[] {
  const start = weekStart(on);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** Six Monday-first weeks covering the month that `on` falls in (42 days). */
export function monthGrid(on: CalendarDate): CalendarDate[] {
  const first = `${on.slice(0, 8)}01` as CalendarDate;
  const start = weekStart(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/** A rotation week is any Monday-to-Sunday week with at least one route placed in it. */
export function isRotationWeek(s: PlannerState, on: CalendarDate): boolean {
  return weekDays(on).some((day) => s.schedule[day] !== undefined);
}

/** Scheduled days from `from` onward, in date order: what the week planner plans. */
export function upcomingRouteDays(s: PlannerState, from: CalendarDate): { on: CalendarDate; route: RouteDef }[] {
  return Object.entries(s.schedule)
    .filter(([on]) => on >= from)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .flatMap(([on, routeId]) => {
      const route = s.routes.find((r) => r.id === routeId);
      return route ? [{ on: on as CalendarDate, route }] : [];
    });
}
